import isEmpty from 'lodash/isEmpty';
import isFinite from 'lodash/isFinite';
import isNil from 'lodash/isNil';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import { MINUTE, HOUR } from '../../constants';
import messages from './messages';

const escapeRegex = (rawString: string) => rawString.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

const durationWithTextRegex = (hourSymbol: string, minuteSymbol: string) =>
	new RegExp(`^\\s*((\\d+)?\\s*${hourSymbol})?\\s*((\\d+)?\\s*(${minuteSymbol})?)?\\s*$`, 'i');

const durationWithColonsRegex = new RegExp('^\\s*(\\d+)?\\s*:?\\s*(\\d+)?\\s*$', 'i');

const parseDurationInt = (durationText?: string) =>
	durationText !== undefined ? parseInt(durationText, 10) : 0;
const getValueFromTimeGoalExpression = (
	inputValue: string,
	formatMessage: FormatMessage,
): number | undefined => {
	let result;
	const hourSymbol = escapeRegex(
		formatMessage(messages.durationInHours, { duration: '' }).trim().toLowerCase(),
	);
	const minuteSymbol = escapeRegex(
		formatMessage(messages.durationInMinutes, { duration: '' }).trim().toLowerCase(),
	);

	let matches = durationWithColonsRegex.exec(inputValue);
	let hours;
	let minutes;
	if (!isNil(matches)) {
		const { '1': h, '2': m } = matches;
		hours = h;
		minutes = m;
	} else {
		matches = durationWithTextRegex(hourSymbol, minuteSymbol).exec(inputValue);
		if (!isNil(matches)) {
			const { '2': h, '4': m } = matches;
			hours = h;
			minutes = m;
		}
	}

	if (!isNil(hours) || !isNil(minutes)) {
		result = (parseDurationInt(hours) * 60 + Math.round(parseDurationInt(minutes))) * 60 * 1000;
		return result;
	}

	return undefined;
};

export const durationToMilliseconds = (
	inputValue: string,
	formatMessage: FormatMessage,
): number | undefined => {
	let ms;
	if (!isEmpty(inputValue)) {
		// check duration value whether is a valid number
		if (isFinite(Number(inputValue))) {
			const goalValue = parseFloat(inputValue);
			const hours = Math.floor(goalValue);
			let mins = goalValue - hours;
			mins = Math.round(mins * 60);

			ms = (hours * 60 + mins) * 60 * 1000;
		} else {
			ms = getValueFromTimeGoalExpression(inputValue.toLowerCase(), formatMessage);
		}
	}
	return ms;
};

export const durationToHumanReadable = (
	timeGoalValueInMs?: number,
	// @ts-expect-error - TS1016 - A required parameter cannot follow an optional parameter.
	formatMessage: FormatMessage,
): string => {
	if (!(isFinite(timeGoalValueInMs) && timeGoalValueInMs !== 0)) {
		return '';
	}
	// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
	const hours = Math.floor(timeGoalValueInMs / HOUR);
	// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
	const minutes = (timeGoalValueInMs % HOUR) / MINUTE;
	const formatted: Array<string> = [];
	if (hours > 0) {
		formatted.push(formatMessage(messages.durationInHours, { duration: hours }));
	}
	if (minutes > 0) {
		formatted.push(formatMessage(messages.durationInMinutes, { duration: minutes }));
	}
	return formatted.join(' ');
};
